/**
 * OrgConfigAuditLog — Phase 16c viewer
 * =====================================
 * Surfaces the S56 AuditLog rows emitted on every OrgConfiguration
 * set/delete (resource_type='OrgConfiguration'). Gives SOX 404 ELC-CFG-01
 * auditors a filterable, before/after view of every org-level configuration
 * mutation with the control_relevant flag highlighted.
 *
 * Endpoint: GET /api/v1/audit/trail?resource_type=OrgConfiguration&...
 * Response: { entries: [...], total, limit, offset }
 * Entry metadata (S56 _entry_to_dict): {id, actor, actor_role, action,
 *   resource_type, resource_id, description, before_state, after_state,
 *   metadata, created_at}. The 16c writers populate metadata with
 *   {key, before_value, after_value, control_relevant: bool}.
 *
 * Degrades gracefully if the endpoint is absent — shows an inline notice
 * rather than crashing the tab host.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { API_V1, authFetch } from '../constants';
import DataTable, { Column } from '../components/ui/DataTable';
import KpiGrid from '../components/ui/KpiGrid';
import Modal from '../components/ui/Modal';

const PRIMARY_ENDPOINT = `${API_V1}/audit/trail`;
const FALLBACK_ENDPOINT = `${API_V1}/admin/audit-log`;
const RESOURCE_TYPE = 'OrgConfiguration';
const ROW_LIMIT = 500;

type ActionFilter = 'ALL' | 'SET' | 'DELETE';
type ControlFilter = 'ALL' | 'ONLY_CONTROL_RELEVANT';
type TimeWindow = '24h' | '7d' | '30d' | '90d';

const WINDOW_HOURS: Record<TimeWindow, number> = {
  '24h': 24,
  '7d': 24 * 7,
  '30d': 24 * 30,
  '90d': 24 * 90,
};

interface RawEntry {
  id?: number | string;
  actor?: string | null;
  actor_role?: string | null;
  actor_ip?: string | null;
  action?: string | null;
  resource_type?: string | null;
  resource_id?: string | null;
  description?: string | null;
  before_state?: Record<string, unknown> | null;
  after_state?: Record<string, unknown> | null;
  metadata?: Record<string, unknown> | null;
  created_at?: string | null;
  // Some shapes expose user_id/user_email/timestamp rather than actor.
  user_id?: number | string | null;
  user_email?: string | null;
  timestamp?: string | null;
  changed_at?: string | null;
}

interface Row {
  id: string;
  timestamp: string | null;
  actor: string;
  action: string;
  key: string;
  beforeValue: unknown;
  afterValue: unknown;
  controlRelevant: boolean;
  requestId: string | null;
  raw: RawEntry;
}

function parseResponse(payload: unknown): RawEntry[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as RawEntry[];
  if (typeof payload === 'object') {
    const obj = payload as Record<string, unknown>;
    for (const k of ['entries', 'rows', 'items', 'results', 'data']) {
      const v = obj[k];
      if (Array.isArray(v)) return v as RawEntry[];
    }
  }
  return [];
}

function pickActor(e: RawEntry): string {
  if (e.actor && String(e.actor).trim()) return String(e.actor);
  if (e.user_email && String(e.user_email).trim()) return String(e.user_email);
  if (e.user_id !== undefined && e.user_id !== null) return `user #${e.user_id}`;
  return '—';
}

function pickTimestamp(e: RawEntry): string | null {
  return e.created_at || e.timestamp || e.changed_at || null;
}

function pickAction(e: RawEntry): string {
  return (e.action || '').toString().toUpperCase() || '—';
}

function pickKey(e: RawEntry): string {
  const md = e.metadata || {};
  const k = (md as Record<string, unknown>).key;
  if (typeof k === 'string' && k.trim()) return k;
  // Fall back to resource_id, which the 16c writer usually sets to the key.
  if (e.resource_id && String(e.resource_id).trim()) return String(e.resource_id);
  return '—';
}

function pickControlRelevant(e: RawEntry): boolean {
  const md = (e.metadata || {}) as Record<string, unknown>;
  return md.control_relevant === true;
}

function pickRequestId(e: RawEntry): string | null {
  const md = (e.metadata || {}) as Record<string, unknown>;
  const rid = md.request_id ?? md.requestId ?? md.req_id;
  return rid === undefined || rid === null ? null : String(rid);
}

function pickBeforeValue(e: RawEntry): unknown {
  const md = (e.metadata || {}) as Record<string, unknown>;
  if ('before_value' in md) return md.before_value;
  if (e.before_state && typeof e.before_state === 'object') {
    const b = e.before_state as Record<string, unknown>;
    if ('value' in b) return b.value;
    return e.before_state;
  }
  return undefined;
}

function pickAfterValue(e: RawEntry): unknown {
  const md = (e.metadata || {}) as Record<string, unknown>;
  if ('after_value' in md) return md.after_value;
  if (e.after_state && typeof e.after_state === 'object') {
    const a = e.after_state as Record<string, unknown>;
    if ('value' in a) return a.value;
    return e.after_state;
  }
  return undefined;
}

function toRow(e: RawEntry, idx: number): Row {
  return {
    id: e.id !== undefined && e.id !== null ? String(e.id) : `row-${idx}`,
    timestamp: pickTimestamp(e),
    actor: pickActor(e),
    action: pickAction(e),
    key: pickKey(e),
    beforeValue: pickBeforeValue(e),
    afterValue: pickAfterValue(e),
    controlRelevant: pickControlRelevant(e),
    requestId: pickRequestId(e),
    raw: e,
  };
}

function fmtValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'string') return v;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

function fmtTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  } catch {
    return iso;
  }
}

function buildIsoSince(window: TimeWindow): string {
  const hours = WINDOW_HOURS[window];
  const ms = Date.now() - hours * 3_600_000;
  return new Date(ms).toISOString();
}

const actionPillStyle = (action: string): React.CSSProperties => {
  const norm = action.toUpperCase();
  if (norm === 'SET') {
    return {
      background: 'rgba(59, 130, 246, 0.12)',
      color: '#1d4ed8',
    };
  }
  if (norm === 'DELETE') {
    return {
      background: 'rgba(239, 68, 68, 0.12)',
      color: '#b91c1c',
    };
  }
  return {
    background: 'rgba(100, 116, 139, 0.12)',
    color: 'var(--text-secondary)',
  };
};

const basePillStyle: React.CSSProperties = {
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '0.02em',
};

const valueCellStyle: React.CSSProperties = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: 12,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  maxWidth: 320,
  display: 'inline-block',
};

export default function OrgConfigAuditLog() {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [endpointMissing, setEndpointMissing] = useState(false);

  const [keyQuery, setKeyQuery] = useState('');
  const [actionFilter, setActionFilter] = useState<ActionFilter>('ALL');
  const [controlFilter, setControlFilter] = useState<ControlFilter>('ALL');
  const [timeWindow, setTimeWindow] = useState<TimeWindow>('30d');

  const [drilldown, setDrilldown] = useState<Row | null>(null);

  const fetchFrom = useCallback(async (url: string): Promise<Response> => {
    return authFetch(url);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setEndpointMissing(false);

    const since = buildIsoSince(timeWindow);
    const qs = new URLSearchParams();
    qs.set('resource_type', RESOURCE_TYPE);
    qs.set('start_date', since);
    qs.set('limit', String(ROW_LIMIT));

    const url = `${PRIMARY_ENDPOINT}?${qs.toString()}`;
    const fallbackUrl = `${FALLBACK_ENDPOINT}?${qs.toString()}`;

    try {
      let res = await fetchFrom(url);
      if (res.status === 404) {
        res = await fetchFrom(fallbackUrl);
      }
      if (res.status === 404) {
        setEndpointMissing(true);
        setRows([]);
        setLoading(false);
        return;
      }
      if (!res.ok) {
        setError(`Audit query failed: HTTP ${res.status}`);
        setRows([]);
        setLoading(false);
        return;
      }
      const body = await res.json();
      const rawEntries = parseResponse(body);
      const parsed: Row[] = rawEntries
        .map((e, i) => toRow(e, i))
        // Defensive: the server filter should already restrict, but just in
        // case a shape comes back without resource_type filter applied.
        .filter((r) => {
          const rt = (r.raw.resource_type || '').toString();
          return !rt || rt === RESOURCE_TYPE;
        });
      setRows(parsed);
    } catch (e) {
      console.error('OrgConfigAuditLog load error', e);
      setError('Failed to load audit log.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fetchFrom, timeWindow]);

  useEffect(() => {
    load();
  }, [load]);

  const filteredRows = useMemo(() => {
    const q = keyQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (actionFilter !== 'ALL' && r.action !== actionFilter) return false;
      if (controlFilter === 'ONLY_CONTROL_RELEVANT' && !r.controlRelevant) return false;
      if (q && !r.key.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [rows, keyQuery, actionFilter, controlFilter]);

  const kpis = useMemo(() => {
    const total = filteredRows.length;
    const controlRelevant = filteredRows.filter((r) => r.controlRelevant).length;
    const uniqueKeys = new Set(filteredRows.map((r) => r.key).filter((k) => k && k !== '—')).size;
    const uniqueActors = new Set(filteredRows.map((r) => r.actor).filter((a) => a && a !== '—')).size;
    return [
      { label: 'Mutations (window)', value: total, color: 'var(--text-primary)' },
      {
        label: 'Control-relevant',
        value: controlRelevant,
        color: controlRelevant > 0 ? '#b45309' : 'var(--text-primary)',
      },
      { label: 'Unique keys', value: uniqueKeys, color: 'var(--text-primary)' },
      { label: 'Unique actors', value: uniqueActors, color: 'var(--text-primary)' },
    ];
  }, [filteredRows]);

  const columns: Column[] = [
    {
      key: 'timestamp',
      label: 'Timestamp',
      render: (_v, row: Row) => (
        <span style={{ fontSize: 12, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
          {fmtTimestamp(row.timestamp)}
        </span>
      ),
    },
    {
      key: 'actor',
      label: 'Actor',
      render: (_v, row: Row) => (
        <span style={{ fontSize: 12 }}>{row.actor}</span>
      ),
    },
    {
      key: 'action',
      label: 'Action',
      render: (_v, row: Row) => (
        <span style={{ ...basePillStyle, ...actionPillStyle(row.action) }}>{row.action}</span>
      ),
    },
    {
      key: 'key',
      label: 'Config Key',
      render: (_v, row: Row) => (
        <span
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 12,
            fontWeight: 600,
            color: 'var(--navy-900)',
          }}
        >
          {row.key}
        </span>
      ),
    },
    {
      key: 'beforeValue',
      label: 'Before',
      render: (_v, row: Row) => (
        <span style={valueCellStyle}>{fmtValue(row.beforeValue)}</span>
      ),
    },
    {
      key: 'afterValue',
      label: 'After',
      render: (_v, row: Row) => (
        <span style={valueCellStyle}>{fmtValue(row.afterValue)}</span>
      ),
    },
    {
      key: 'controlRelevant',
      label: 'Control-relevant',
      render: (_v, row: Row) =>
        row.controlRelevant ? (
          <span
            style={{
              ...basePillStyle,
              background: 'rgba(245, 158, 11, 0.14)',
              color: '#b45309',
            }}
            title="Mutation of a SOX 404 control-relevant key (ELC-CFG-01)"
          >
            SOX 404 control-relevant
          </span>
        ) : (
          <span style={{ fontSize: 11, color: 'var(--text-hint)' }}>No</span>
        ),
    },
    {
      key: 'requestId',
      label: 'Request ID',
      render: (_v, row: Row) => (
        <span
          style={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            color: 'var(--text-hint)',
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'inline-block',
          }}
          title={row.requestId || undefined}
        >
          {row.requestId ? row.requestId.slice(0, 12) : '—'}
        </span>
      ),
    },
  ];

  const filterBarStyle: React.CSSProperties = {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 12,
    alignItems: 'flex-end',
    padding: 16,
    background: 'var(--bg-card)',
    border: '1px solid var(--border-subtle)',
    borderRadius: 8,
    marginBottom: 16,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: 'var(--text-hint)',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    marginBottom: 4,
    display: 'block',
  };

  const inputStyle: React.CSSProperties = {
    padding: '6px 10px',
    fontSize: 13,
    border: '1px solid var(--border-default)',
    borderRadius: 6,
    background: '#fff',
    color: 'var(--text-primary)',
    minWidth: 200,
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    minWidth: 160,
    cursor: 'pointer',
  };

  if (endpointMissing) {
    return (
      <div
        style={{
          padding: 24,
          background: 'var(--bg-card)',
          border: '1px dashed var(--border-default)',
          borderRadius: 8,
          color: 'var(--text-secondary)',
          fontSize: 13,
          lineHeight: 1.6,
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: 6, color: 'var(--navy-900)' }}>
          Audit log endpoint not available
        </div>
        This view requires the S56 AuditLog endpoint (Phase 16c handover).
        Neither <code>{PRIMARY_ENDPOINT}</code> nor <code>{FALLBACK_ENDPOINT}</code>
        responded. Deploy the audit router or verify the API mount path.
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: 'rgba(245, 158, 11, 0.08)',
          border: '1px solid rgba(245, 158, 11, 0.3)',
          borderRadius: 6,
          fontSize: 12,
          color: 'var(--text-secondary)',
          lineHeight: 1.5,
        }}
      >
        <strong style={{ color: '#b45309' }}>ELC-CFG-01 — Configuration change monitoring.</strong>{' '}
        Every OrgConfiguration set/delete emits an immutable S56 AuditLog row
        with before/after value, actor, timestamp, and the SOX 404
        control-relevant flag. This view is the auditor's lookup for
        configuration change evidence.
      </div>

      <div style={filterBarStyle}>
        <div>
          <label style={labelStyle}>Key search</label>
          <input
            type="text"
            placeholder="filter by config key…"
            value={keyQuery}
            onChange={(e) => setKeyQuery(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>Action</label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value as ActionFilter)}
            style={selectStyle}
          >
            <option value="ALL">All actions</option>
            <option value="SET">SET</option>
            <option value="DELETE">DELETE</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Scope</label>
          <select
            value={controlFilter}
            onChange={(e) => setControlFilter(e.target.value as ControlFilter)}
            style={selectStyle}
          >
            <option value="ALL">All mutations</option>
            <option value="ONLY_CONTROL_RELEVANT">Only control-relevant</option>
          </select>
        </div>
        <div>
          <label style={labelStyle}>Time range</label>
          <select
            value={timeWindow}
            onChange={(e) => setTimeWindow(e.target.value as TimeWindow)}
            style={selectStyle}
          >
            <option value="24h">Last 24 hours</option>
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <button
            onClick={load}
            style={{
              padding: '8px 16px',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 6,
              border: '1px solid var(--border-default)',
              background: '#fff',
              color: 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      <KpiGrid items={kpis} columns={4} />

      {error && (
        <div
          style={{
            padding: 12,
            marginBottom: 12,
            background: 'rgba(239, 68, 68, 0.08)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            borderRadius: 6,
            color: '#b91c1c',
            fontSize: 12,
          }}
        >
          {error}
        </div>
      )}

      <DataTable
        columns={columns}
        rows={filteredRows}
        loading={loading}
        emptyMessage={
          rows.length === 0
            ? 'No OrgConfiguration mutations in this window.'
            : 'No rows match the current filters.'
        }
        rowKey="id"
        onRowClick={(r) => setDrilldown(r as Row)}
      />

      {drilldown && (
        <Modal
          open
          onClose={() => setDrilldown(null)}
          title={`Audit entry — ${drilldown.key}`}
          description={`${fmtTimestamp(drilldown.timestamp)} · ${drilldown.actor} · ${drilldown.action}`}
          size="lg"
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={labelStyle}>Description</div>
              <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>
                {drilldown.raw.description || '—'}
              </div>
            </div>

            <div>
              <div style={labelStyle}>Metadata (S56 audit_metadata)</div>
              <pre
                style={{
                  background: 'var(--bg-hover)',
                  border: '1px solid var(--border-subtle)',
                  borderRadius: 6,
                  padding: 12,
                  fontSize: 12,
                  margin: 0,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {JSON.stringify(drilldown.raw.metadata ?? {}, null, 2)}
              </pre>
            </div>

            {drilldown.raw.before_state && (
              <div>
                <div style={labelStyle}>Before state</div>
                <pre
                  style={{
                    background: 'var(--bg-hover)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 6,
                    padding: 12,
                    fontSize: 12,
                    margin: 0,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(drilldown.raw.before_state, null, 2)}
                </pre>
              </div>
            )}

            {drilldown.raw.after_state && (
              <div>
                <div style={labelStyle}>After state</div>
                <pre
                  style={{
                    background: 'var(--bg-hover)',
                    border: '1px solid var(--border-subtle)',
                    borderRadius: 6,
                    padding: 12,
                    fontSize: 12,
                    margin: 0,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(drilldown.raw.after_state, null, 2)}
                </pre>
              </div>
            )}

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 12,
                fontSize: 12,
              }}
            >
              <div>
                <div style={labelStyle}>Actor role</div>
                <div>{drilldown.raw.actor_role || '—'}</div>
              </div>
              <div>
                <div style={labelStyle}>Actor IP</div>
                <div style={{ fontFamily: 'ui-monospace, monospace' }}>
                  {drilldown.raw.actor_ip || '—'}
                </div>
              </div>
              <div>
                <div style={labelStyle}>Resource ID</div>
                <div style={{ fontFamily: 'ui-monospace, monospace' }}>
                  {drilldown.raw.resource_id || '—'}
                </div>
              </div>
              <div>
                <div style={labelStyle}>Entry ID</div>
                <div style={{ fontFamily: 'ui-monospace, monospace' }}>
                  {drilldown.id}
                </div>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
